// Email send DAO — data access for email sends and attachments.
// Used by both FE and edge functions (email-send, sheets-import).

import { BaseDao, type SupabaseClient, type InvalidateFn, type MutationOptions } from '../base.ts';
import type { EmailSendRow, EmailSendInsert, EmailAttachmentInsert, EmailType } from './types.ts';

// ── EmailSendDao ────────────────────────────────────────────────────────

export class EmailSendDao extends BaseDao<EmailSendRow> {
  protected readonly dependentResources = [
    'email_sends',
    'email_attachments',
  ];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'email_sends', invalidate);
  }

  /** List emails for an invoice, sorted by sent_at desc, created_at desc. */
  async listByInvoice(invoiceId: number): Promise<EmailSendRow[]> {
    const { data, error } = await this.sb
      .from('email_sends')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('sent_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data as EmailSendRow[]) ?? [];
  }

  /** Count emails for a transaction. */
  async countByTransaction(transactionId: number): Promise<number> {
    const { count, error } = await this.sb
      .from('email_sends')
      .select('id', { count: 'exact', head: true })
      .eq('transaction_id', transactionId);
    if (error) throw error;
    return count ?? 0;
  }

  /** Get the latest email of a given type for a transaction. */
  async getLatestByType(transactionId: number, emailType: EmailType): Promise<EmailSendRow | null> {
    const { data, error } = await this.sb
      .from('email_sends')
      .select('*')
      .eq('transaction_id', transactionId)
      .eq('email_type', emailType)
      .order('created_at', { ascending: false })
      .limit(1)
      .single<EmailSendRow>();
    if (error && error.code === 'PGRST116') return null; // no rows
    if (error) throw error;
    return data;
  }

  /** Create an email send with optional attachments (junction table). */
  async createWithAttachments(
    email: EmailSendInsert,
    attachmentFileIds?: number[],
    opts?: MutationOptions,
  ): Promise<EmailSendRow> {
    const { data: created, error } = await this.sb
      .from('email_sends')
      .insert(email as Record<string, unknown>)
      .select('*')
      .single<EmailSendRow>();
    if (error) throw error;

    if (attachmentFileIds && attachmentFileIds.length > 0) {
      const attachments: EmailAttachmentInsert[] = attachmentFileIds.map(fileId => ({
        email_send_id: created.id,
        transaction_file_id: fileId,
      }));
      const { error: attachError } = await this.sb
        .from('email_attachments')
        .insert(attachments as Record<string, unknown>[]);
      if (attachError) throw attachError;
    }

    this.handleInvalidation(opts);
    return created;
  }

  /**
   * Check if any reminder email of the given types was already sent today for a transaction.
   * Returns the sent_at timestamp of the most recent match, or null if none found today.
   */
  async findRecentReminderToday(
    transactionId: number,
    emailTypes: EmailType[],
  ): Promise<string | null> {
    const { data, error } = await this.sb
      .from('email_sends')
      .select('sent_at')
      .eq('transaction_id', transactionId)
      .in('email_type', emailTypes)
      .not('sent_at', 'is', null)
      .order('sent_at', { ascending: false })
      .limit(1);
    if (error) throw error;
    const sentAt = (data?.[0] as { sent_at: string } | undefined)?.sent_at ?? null;
    if (!sentAt) return null;
    const today = new Date().toDateString();
    return new Date(sentAt).toDateString() === today ? sentAt : null;
  }

  /** Bulk insert email sends (no attachments). */
  async bulkInsert(emails: EmailSendInsert[], opts?: MutationOptions): Promise<void> {
    if (emails.length === 0) return;
    const BATCH_SIZE = 50;
    for (let i = 0; i < emails.length; i += BATCH_SIZE) {
      const batch = emails.slice(i, i + BATCH_SIZE);
      const { error } = await this.sb
        .from('email_sends')
        .insert(batch as Record<string, unknown>[]);
      if (error) throw error;
    }
    this.handleInvalidation(opts);
  }

  /**
   * Record a system send — the documents-reminder sweep (or any future
   * unattended sender), never an operator. `user_id` is always null (the
   * documents-reminder migration dropped the NOT NULL constraint precisely
   * for this); `from_address` is the dedicated sender mailbox, not a
   * per-employee address. `sent_at` defaults to now — pass it explicitly only
   * when the caller already has the mailer's own timestamp.
   */
  async recordSystemSend(
    params: {
      transactionId: number;
      emailType: EmailType;
      recipients: string[];
      cc: string[];
      subject: string | null;
      htmlBody: string | null;
      fromAddress: string;
      gmailMessageId?: string | null;
      gmailThreadId?: string | null;
      sentAt?: string;
    },
    opts?: MutationOptions,
  ): Promise<EmailSendRow> {
    const email: EmailSendInsert = {
      user_id: null,
      email_type: params.emailType,
      transaction_id: params.transactionId,
      invoice_id: null,
      recipients: params.recipients,
      cc: params.cc,
      subject: params.subject,
      html_body: params.htmlBody,
      gmail_message_id: params.gmailMessageId ?? null,
      gmail_thread_id: params.gmailThreadId ?? null,
      from_address: params.fromAddress,
      created_at: new Date().toISOString(),
      sent_at: params.sentAt ?? new Date().toISOString(),
    };
    const { data, error } = await this.sb
      .from('email_sends')
      .insert(email as Record<string, unknown>)
      .select('*')
      .single<EmailSendRow>();
    if (error) throw error;
    this.handleInvalidation(opts);
    return data;
  }
}
